import React from 'react';
import CgSearchFilter from 'modules/Common/ui/CgSearchFilter';
import { useTranslation } from 'react-i18next';
import { REPORTING_YEAR_OPTIONS } from '../state/mockData';

// Filter set for the emissions-data table, built on the shared CgSearchFilter
// so the funnel popover, active-filter chips and per-column filter icons all
// come from the same machinery the real tables use.
//
// `cnCodeOptions` (Item 16B, fixpack 2026-07-23): the CN Code filter used to
// read the demo-era CN_CODE_OPTIONS — a hardcoded 6-entry steel/aluminium
// list that couldn't offer any other real CN code as a filter at all. The
// caller (EmissionFactorsPage) now derives this from the distinct cnCodes
// actually present in the loaded factors, labeled via useCnGoodLabel — so
// the dropdown only ever offers codes the supplier can actually filter by.
export default function EmissionsDataFilters({
  form,
  onFormChange,
  onReset,
  installationOptions = [],
  cnCodeOptions = [],
  onFiltersReady,
  tableKey,
  tableTitle,
  tableContext,
}) {
  const { t } = useTranslation('freeSupplier');
  const filters = [
    {
      type: 'multi-select',
      name: 'year',
      label: t('emissionsDataFilters.yearLabel', 'Year'),
      // Year, CN code, and product ID all live in the merged "Product" column.
      columnKey: 'product',
      options: REPORTING_YEAR_OPTIONS.map((y) => ({ value: y, label: y })),
      searchable: false,
      className: 'w-[260px] pb-3',
      customValueRenderer: (selected) =>
        selected.length
          ? selected.length === 1
            ? selected[0]
            : t('emissionsDataFilters.yearsSelectedCount', {
                count: selected.length,
                defaultValue: '{{count}} year selected',
                defaultValue_other: '{{count}} years selected',
              })
          : t('emissionsDataFilters.yearLabel', 'Year'),
    },
    {
      type: 'multi-select',
      name: 'cnCode',
      label: t('emissionsDataFilters.cnCodeLabel', 'CN Code'),
      columnKey: 'product',
      options: cnCodeOptions,
      className: 'w-[260px] pb-3',
      customValueRenderer: (selected) =>
        selected.length
          ? t('emissionsDataFilters.cnCodesSelectedCount', {
              count: selected.length,
              defaultValue: 'Selected {{count}} CN code',
              defaultValue_other: 'Selected {{count}} CN codes',
            })
          : t('emissionsDataFilters.cnCodeLabel', 'CN Code'),
    },
    {
      type: 'select',
      name: 'installationId',
      label: t('emissionsDataFilters.installationLabel', 'Installation'),
      options: installationOptions,
      searchable: true,
      className: 'w-[260px] pb-3',
    },
    {
      type: 'text',
      name: 'productId',
      label: t('emissionsDataFilters.productIdLabel', 'Product ID'),
      columnKey: 'product',
      placeholder: t('emissionsDataFilters.productIdPlaceholder', 'Search by product ID…'),
      className: 'w-[260px] pb-3',
    },
    {
      type: 'select',
      name: 'source',
      label: t('emissionsDataFilters.sourceLabel', 'Source'),
      columnKey: 'source',
      options: [
        {
          value: 'Manual entry',
          label: t('emissionsDataFilters.sourceManualEntry', 'Manual entry'),
        },
        {
          value: 'Communication template',
          label: t('emissionsDataFilters.sourceCommsTemplate', 'Communication template'),
        },
      ],
      className: 'w-[260px] pb-3',
    },
  ];

  return (
    <CgSearchFilter
      filters={filters}
      formData={form}
      onFormChange={onFormChange}
      onSubmit={() => {}}
      onReset={onReset}
      onFiltersReady={onFiltersReady}
      tableTitle={tableTitle}
      tableKey={tableKey}
      tableContext={tableContext}
    />
  );
}
